<script lang="ts">import { resolvePromotionDescriptionDisplay } from "../../data";
import { cfgBrandTitle, stateUi } from "@infinitro/config";
import { locale, t, Verbiage } from "@infinitro/content";
import { formatWithHighlight, hexToRgba, isMobile, toTitleCase } from "@infinitro/global";
export let promo;
export let isDetail;
export let shouldHighlight = true;
const BRAND_NAME = cfgBrandTitle();
function titleCasePromoHeading(raw) {
	const trimmed = raw.trim();
	const bracket = trimmed.match(/^(.*)\[([^\]]+)\](.*)$/);
	if (bracket) {
		const before = bracket[1].trim();
		const inner = toTitleCase(bracket[2].trim());
		const after = toTitleCase(bracket[3].trim());
		const mid = `[${inner}]`;
		return [
			before,
			mid,
			after
		].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
	}
	return toTitleCase(trimmed);
}
/* ---------- Reactive helpers ---------- */
$: promoDescriptionDisplay = resolvePromotionDescriptionDisplay(promo, $locale, $t);
$: useUppercaseSectionTitles = $stateUi.layout.useUppercaseSectionTitles ?? true;
$: useTitleHighlight = shouldHighlight && ($stateUi.promotions.highlightTitleFirstWord ?? true);
$: rawPromoTitle = $t(promo.title)?.replace("[BRAND]", BRAND_NAME) ?? "";
$: hasExplicitLineBreak = rawPromoTitle.includes("\n") || rawPromoTitle.includes("\\n");
$: translatedTitle = useUppercaseSectionTitles ? rawPromoTitle.toUpperCase() : hasExplicitLineBreak ? rawPromoTitle : titleCasePromoHeading(rawPromoTitle);
let firstWords = "";
let restWords = "";
$: titleSingleLine = promo.titleSingleLine === true;
$: normalizedTitle = translatedTitle?.replace(/\\n/g, "\n")?.replace(/\r\n?/g, "\n");
$: {
	if (normalizedTitle?.includes("\n")) {
		const [firstLine, ...otherLines] = normalizedTitle.split("\n").map((line) => line.trim()).filter(Boolean);
		firstWords = firstLine ?? "";
		restWords = otherLines.join(" ");
	} else if (normalizedTitle?.includes("[") && normalizedTitle?.includes("]")) {
		const match = normalizedTitle.match(/\[([^\]]+)\](.*)/);
		if (match) {
			firstWords = match[1].trim();
			restWords = match[2].trim();
		}
	} else if (normalizedTitle) {
		const parts = normalizedTitle.split(" ");
		firstWords = parts[0];
		restWords = parts.slice(1).join(" ");
	}
}
$: usePromoBannerStyleAlternative2 = $stateUi.promotions.promoBannerStyle === "alternative2";
$: hasAmountBox = !!promo.amountBox?.text;
$: amountBoxText = promo.amountBox?.text ? $t(promo.amountBox.text) : "";
$: amountBoxColor = promo.amountBox?.color ?? "var(--color-text-100)";
$: amountBoxTextColor = promo.amountBox?.textColor ?? "var(--color-background-900)";
</script>

{#if !promo.onlyImage}
	{#if $isMobile}
		<!-- ─────────── Mobile ─────────── -->
		<div
			class="mobile-banner-content"
			class:mobile-banner-content-alternative2={usePromoBannerStyleAlternative2}
		>
			<div>
				<div class="mobile-banner-title" style="width: {promo.customTitleWidth ?? ''}">
					<p
						class="mobile-banner-title-text promo-title-size"
						class:mobile-banner-title-text-alternative2={usePromoBannerStyleAlternative2}
					>
						{#if titleSingleLine || hasAmountBox}
							<span class:alternative2-title-word={usePromoBannerStyleAlternative2}>
								{translatedTitle}
							</span>
						{:else if usePromoBannerStyleAlternative2}
							<span class="alternative2-title-word alternative2-title-multiline">
								{normalizedTitle}
							</span>
						{:else if useTitleHighlight}
							<span class="highlight-word">{firstWords}</span>
							<span class="rest-title">{restWords}</span>
						{:else}
							{translatedTitle}
						{/if}
					</p>
				</div>

				{#if hasAmountBox}
					<div
						class="amount-box promo-title-size"
						style="background-color: {amountBoxColor}; color: {amountBoxTextColor};"
					>
						{amountBoxText}
					</div>
				{/if}

				<div
					class="mobile-description-text mobile-hide-tablet"
					class:mobile-description-text-referral={promo.id === 'referral'}
					class:description-text-alternative2={usePromoBannerStyleAlternative2}
				>
					{@html formatWithHighlight(promoDescriptionDisplay?.replace(/\n/g, '<br>'))}
				</div>
			</div>
		</div>
	{:else}
		<!-- ─────────── Desktop ─────────── -->
		<div class="banner-content" class:banner-content-alternative2={usePromoBannerStyleAlternative2}>
			<div class="banner-margin">
				<div class="banner-title" style="width: {promo.customTitleWidth ?? ''}">
					<h2
						class="banner-title-text promo-title-size"
						class:banner-title-text-detail={isDetail}
						class:banner-title-text-alternative2={usePromoBannerStyleAlternative2}
						class:banner-title-text-detail-alternative2={isDetail &&
							usePromoBannerStyleAlternative2}
					>
						{#if titleSingleLine || isDetail || hasAmountBox}
							<span class:alternative2-title-word={usePromoBannerStyleAlternative2}>
								{translatedTitle}
							</span>
						{:else if usePromoBannerStyleAlternative2 && useTitleHighlight}
							<span class="alternative2-title-word alternative2-title-multiline">
								{normalizedTitle}
							</span>
						{:else if useTitleHighlight}
							<span class="highlight-word">{firstWords}</span>
							<span class="rest-title">{restWords}</span>
						{:else}
							{translatedTitle}
						{/if}
					</h2>
				</div>

				{#if hasAmountBox}
					<div
						class="amount-box promo-title-size"
						style="background-color: {amountBoxColor}; color: {amountBoxTextColor};"
					>
						{amountBoxText}
					</div>
				{/if}

				<div
					class="description-text"
					class:description-text-detail={isDetail}
					class:description-text-alternative2={usePromoBannerStyleAlternative2}
				>
					{@html formatWithHighlight(
						isDetail
							? promoDescriptionDisplay?.replace(/\n/g, ' ')
							: promoDescriptionDisplay?.replace(/\n/g, '<br>')
					)}
				</div>
			</div>
		</div>
	{/if}
{/if}

<style>
	/* ─────── General ─────── */
	.rest-title {
		display: block;
		line-height: 1.25;
}

	/* ─────── Desktop ─────── */
	.banner-content {
		position: relative;
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		justify-content: flex-start;
		padding: 1rem;
		z-index: 50;
}

	.banner-content-alternative2 {
		justify-content: center;
		gap: 1rem;
		padding-left: 1rem;
		padding-right: 1rem;
}

	.banner-title {
		margin-bottom: 0.5rem;
		display: flex;
		width: 100%;
		justify-content: flex-start;
		font-size: 22px;
		font-weight: 700;
		color: white;
}

	.description-text {
		margin-bottom: 0.5rem;
		width: 70%;
		font-size: 14px;
}

	.description-text-detail {
		width: 100%;
}

	.description-text-alternative2 {
		font-weight: 600;
		text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.8);
		line-height: 1.2;
	}

	.banner-title-text {
		display: flex;
		flex-direction: column;
		font-weight: 800;
		line-height: 1.25;
		font-family: var(--font-alternative-2);
}

	.banner-title-text-detail .alternative2-title-word {
		font-size: 40px;
		font-weight: 800;
		font-family: var(--font-alternative-2);
		text-align: left;
}

	.banner-title-text-alternative2 {
		max-width: 75%;
		font-weight: 600;
}

	@media (min-width: 1200px) {
		.banner-title-text {
				display: flex;
		}
	}

	.banner-title-text-detail-alternative2 {
		width: 380px;
		max-width: none;
	}

	.banner-margin {
		margin-bottom: 0.5rem;
}

	/* ─────── Mobile ─────── */

	.mobile-banner-content {
		position: relative;
		z-index: 10;
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		padding: 1rem;
}

	.mobile-banner-content-alternative2 {
		padding-left: 1rem;
		padding-right: 1rem;
}

	.mobile-banner-title {
		margin-bottom: 0.5rem;
		display: flex;
		width: 100%;
		justify-content: flex-start;
		color: white;
}

	.mobile-banner-title-text {
		display: flex;
		flex-direction: column;
		font-weight: 600;
		line-height: 1.25;
		font-family: var(--font-alternative-2);
}

	.mobile-banner-title-text-alternative2 {
		max-width: 75%;
		font-weight: 600;
}

	.mobile-description-text {
		margin-bottom: 0.5rem;
		width: 62%;
		font-size: 14px;
}

	.mobile-description-text-referral {
		margin-bottom: 0.5rem;
		width: 53%;
		font-size: 14px;
}

	.mobile-description-text.description-text-alternative2 {
		text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.8);
	}

	.description-text :global(.promotion-period-range-label),
	.mobile-description-text :global(.promotion-period-range-label) {
		opacity: 0.8;
		font-weight: 400;
	}

	.description-text :global(.promotion-period-range-value),
	.mobile-description-text :global(.promotion-period-range-value) {
		opacity: 1;
		font-weight: 700;
	}

	/* ─────── Highlight helpers ─────── */
	.highlight-word {
		color: var(--color-highlight-1);
	}

	.rest-title {
		color: white;
	}

	/* ─────── Alternative2 (casino-top-card style) ─────── */
	.promo-badge {
		width: -moz-fit-content;
		width: fit-content;
		border-radius: 4px;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
		padding-top: 0.125rem;
		padding-bottom: 0.125rem;
		font-size: 12px;
		font-weight: 800;
		text-transform: uppercase;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
		background: linear-gradient(90deg, #ffffff 0%, #ffffffb3 100%);
}

	.alternative2-title-word {
		font-size: 24px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-weight: 700;
		line-height: 1.2;
}

	.alternative2-title-multiline {
		white-space: pre-line;
	}

	/* ─────── Amount box ─────── */
	.amount-box {
		margin-bottom: 0.5rem;
		display: inline-block;
		width: -moz-fit-content;
		width: fit-content;
		border-radius: 6.5px;
		padding-left: 0.25rem;
		padding-right: 0.25rem;
		padding-top: 0.125rem;
		padding-bottom: 0.125rem;
		font-weight: 800;
		font-family: var(--font-alternative-2);
		line-height: 1.3;
}

	/*
	 * Single source of truth for the title/amount font-size: both the title
	 * (mobile-banner-title-text / banner-title-text) and the amount box share
	 * this class, so they can never drift out of sync — override this once
	 * (e.g. per brand) instead of duplicating the value in two places.
	 */
	.promo-title-size {
		font-size: 22px;
}

	@media (min-width: 768px) {
		.promo-title-size {
				font-size: 30px;
		}
	}
</style>
